/**
 * A pack, rendered as the actual 3D wrapper wherever one would otherwise be a
 * flat picture — shop tiles, the hero, the pack page.
 *
 * All of these share one WebGLRenderer (see `lib/pack3d.ts`), so a grid of
 * twenty-one tiles is twenty-one cheap 2D canvases and one GL context, not
 * twenty-one contexts. Instances only redraw while something is moving, so an
 * idle grid costs nothing per frame.
 *
 * If WebGL or the model is unavailable this falls back to the flat artwork,
 * which is why every caller still passes it.
 */
import { useEffect, useRef, useState } from 'react'

import * as pack3d from '../lib/pack3d'
import type { PackSummary } from '../lib/api'
import { TIER_ACCENT, TIER_LABEL } from './ui'

export function Pack3D({
  pack,
  className = '',
  idle = true,
  /** Rendered underneath until the GL pack is up, and kept if it never is. */
  fallbackClassName = '',
}: {
  pack: PackSummary
  className?: string
  idle?: boolean
  fallbackClassName?: string
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [live, setLive] = useState(false)
  const accent = TIER_ACCENT[pack.tier]

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    let cancelled = false

    pack3d
      .mount(host, { accent, tier: pack.tier, art: pack.art, tearable: false, idle })
      .then((ok) => {
        if (!cancelled) setLive(ok)
      })
      .catch(() => setLive(false))

    return () => {
      cancelled = true
      pack3d.unmount(host)
    }
  }, [accent, idle, pack.art, pack.tier])

  // The catalogue can re-render with new art without the host remounting.
  useEffect(() => {
    const host = hostRef.current
    if (host && live) pack3d.setPackArt(host, pack.art)
  }, [live, pack.art])

  return (
    <div className={`relative ${className}`}>
      {!live && <FlatArt pack={pack} accent={accent} className={fallbackClassName} />}
      <div ref={hostRef} className="absolute inset-0" />
    </div>
  )
}

/** The 2D pack: generated artwork if there is any, procedural foil if not. */
export function FlatArt({
  pack,
  accent,
  className = '',
}: {
  pack: PackSummary
  accent: string
  className?: string
}) {
  if (pack.art) {
    return (
      <img
        src={pack.art}
        alt=""
        loading="lazy"
        className={`absolute inset-0 size-full object-contain ${className}`}
      />
    )
  }

  return (
    <div
      className={`absolute inset-0 grid place-items-center ${className}`}
      style={{
        background: `
          radial-gradient(120% 90% at 50% -20%, ${accent}26 0%, transparent 62%),
          repeating-linear-gradient(58deg, ${accent}12 0 2px, transparent 2px 11px),
          var(--color-surface-2)
        `,
      }}
    >
      <div className="px-4 text-center">
        <div className="text-[0.625rem] font-bold tracking-[0.2em] uppercase" style={{ color: accent }}>
          Elyxr
        </div>
        <div className="mt-1.5 text-lg font-bold tracking-tight">{TIER_LABEL[pack.tier]}</div>
      </div>
    </div>
  )
}
